import { PieChart, Pie, Tooltip, Cell, Sector } from "recharts";

import {
  generateCategoryName,
  getChargedAmmountInILS,
  type Transaction,
} from "./chartDataFunctions";
import type { FiatRateHistory } from "~/calculations/genericCalculations/convertCurrencyOnDate";
import type { SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";
import ChartContainer from "../UI/ChartContainer";
import ChartTitle from "../UI/ChartTitle";
import { useLanguage } from "~/hooks/useLanguage";
import { useCurrency } from "~/hooks/useCurrency";
import type { Language } from "~/utils/language";
import { cashflowCharts } from "~/translations/cashflow/cashflowCharts";
import { Tabs } from "~/components/ui";
import { CashflowMixpanelEvents } from "../assetsChart/mixpanel/mixpanelEvents";
import useTracking from "~/hooks/useTracking";
import type { ContextProps, Fiat } from "~/types/global.types";
import { useOutletContext } from "@remix-run/react";
import {
  hash as _hash,
  stringToColor as _stringToColor,
  CATEGORY_PALETTE,
} from "~/constants/categoryColors";
import BudgetCategoryIcon from "~/components/budgeting/BudgetCategoryIcon";
import tinycolor from "tinycolor2";
import { useCashflowDispatch, useCashflowOptional } from "./cashflowContext";
import CashflowDateRangeLabel from "./components/CashflowDateRangeLabel";

type TransactionsSumaryChartProps = {
  CustomTooltip: any;
  transactions: Transaction[];
  title: string;
  InitialCategory?: string;
  categoryType: string;
  month: string;
  column: "expenses" | "income";
  /** Static PDF-report mode: hides the category-type toggle and disables clicks. */
  report?: boolean;
};

const TransactionSummaryChart = ({
  CustomTooltip,
  month,
  transactions,
  title,
  InitialCategory,
  categoryType,
  column,
  report,
}: TransactionsSumaryChartProps) => {
  const { lang } = useLanguage();
  const { formatValue } = useCurrency();
  const dispatch = useCashflowDispatch();
  const resolvedInitialCategory =
    InitialCategory ?? cashflowCharts[lang].Categories.All;
  const { fiat } = useOutletContext<ContextProps>();
  const fiatHistory = useCashflowOptional()?.fiatHistory ?? undefined;
  let [category, setCategory] = useState(resolvedInitialCategory);
  let [activeIndex, setActiveIndex] = useState<number | undefined>();

  const categoryDataArray = generateExpensesByCategoryData(
    lang,
    transactions,
    fiat,
    fiatHistory
  );
  const categoryTotalsKey = categoryDataArray
    .map((i) => `${i.name}:${i.value}`)
    .join("|");

  const tracking = useTracking();

  const switchCategoryType = (newCategoryType: string) => {
    dispatch({
      type: "SET_CATEGORY_TYPE",
      payload: { column, categoryType: newCategoryType },
    });
    tracking.track(CashflowMixpanelEvents.SWITH_CATEGORY_TYPE);
  };

  useEffect(() => {
    setActiveIndex(
      getCategoryIndexByName(categoryDataArray, resolvedInitialCategory)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedInitialCategory, categoryTotalsKey]);

  useEffect(() => {
    setActiveIndex(undefined);
    dispatch({
      type: "SET_CATEGORY_FILTER",
      payload: { column, category: cashflowCharts[lang].Categories.All },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryType]);

  const categoryData = categoryDataArray.filter(
    (item) => item.value && item.value > 0
  );
  const hasCategoryData = categoryData.length > 0;
  const emptyCategoryData = [
    {
      name: cashflowCharts[lang].categoryChartEmptyState,
      value: 1,
      color: "#dbe8ec",
    },
  ];
  const chartData = hasCategoryData ? categoryData : emptyCategoryData;

  const onMouseClick = useCallback(
    (data: any, index: any) => {
      if (data.name === category) {
        setCategory(cashflowCharts[lang].Categories.All);
        dispatch({
          type: "SET_CATEGORY_FILTER",
          payload: { column, category: cashflowCharts[lang].Categories.All },
        });
        setActiveIndex(undefined);
      } else {
        setCategory(data.name);
        dispatch({
          type: "SET_CATEGORY_FILTER",
          payload: { column, category: data.name },
        });
        setActiveIndex(index);
      }
    },
    [dispatch, column, category, lang]
  );
  let categoryTypes = [
    { id: 0, name: cashflowCharts[lang].CategoryTypes.Sub },
    { id: 1, name: cashflowCharts[lang].CategoryTypes.Main },
  ];
  const selectedCategoryTypeIndex = Math.max(
    0,
    categoryTypes.findIndex((t) => t.name === categoryType)
  );

  return (
    <div className="col-span-5 row-span-5">
      <ChartContainer alignTop={true} minimizeID={title}>
        <div className="flex flex-col items-center justify-center">
          <div
            className={`mb-1 flex w-full flex-wrap items-start justify-between gap-x-3 gap-y-2 px-2 mobile:flex-col mobile:items-center${
              lang === "ENG" ? " pl-4" : ""
            }`}
          >
            <div className="mb-2 flex min-w-[150px] max-w-full flex-col items-center">
              <ChartTitle text={title} classNames="whitespace-nowrap" />
              <CashflowDateRangeLabel
                month={month}
                lang={lang}
                className="whitespace-nowrap border-none bg-transparent text-lg font-normal leading-5 text-gray-500 mobile:text-base"
              />
            </div>
            {!report && (
              <div
                dir={lang === "HEB" ? "rtl" : "ltr"}
                className="ms-2 mt-2 mobile:ms-0"
              >
                <Tabs
                  variant="segmented"
                  className="mb-[1.5rem] min-h-[32px] md:mb-[0rem]"
                  value={categoryTypes[selectedCategoryTypeIndex].name}
                  onChange={(name) => switchCategoryType(name)}
                  items={categoryTypes.map((categoryType) => ({
                    value: categoryType.name,
                    label: categoryType.name,
                  }))}
                />
              </div>
            )}
          </div>
          <div className="border-none bg-transparent font-Rubik text-[30px] font-[500] text-menu">
            <div className="md-ms-8 md-me-8">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div className="relative">
                  <PieChart
                    width={350}
                    height={270}
                    margin={{ top: 5, right: 2, bottom: 5, left: 2 }}
                  >
                    <Pie
                      className={hasCategoryData ? "cursor-pointer" : ""}
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={false}
                      innerRadius={72}
                      outerRadius={100}
                      dataKey="value"
                      paddingAngle={1.5}
                      cornerRadius={6}
                      minAngle={10}
                      activeIndex={hasCategoryData ? activeIndex : undefined}
                      activeShape={
                        hasCategoryData ? renderActiveShape : undefined
                      }
                      onClick={
                        hasCategoryData && !report ? onMouseClick : undefined
                      }
                    >
                      {chartData.map((entry, index: number) => {
                        return (
                          <Cell
                            key={`cell-${index}`}
                            style={{ outline: "none" }}
                            fill={entry.color}
                          />
                        );
                      })}
                    </Pie>
                    {hasCategoryData && <Tooltip content={<CustomTooltip />} />}
                  </PieChart>

                  {!hasCategoryData && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-20 text-center">
                      <p className="font-Rubik text-xl font-semibold leading-7 text-menu">
                        {cashflowCharts[lang].categoryChartEmptyState}
                      </p>
                    </div>
                  )}
                </div>

                {hasCategoryData && (
                  <div className="grid-rows-2 ms-4 grid grid-cols-3">
                    {categoryDataArray.map((item, index: number) => {
                      const isActive = activeIndex === index;
                      const circleSize = isActive ? "w-8 h-8" : "w-7 h-7";
                      let nameClass =
                        "font-Rubik text-base font-medium text-menu";
                      let valueClass =
                        "font-Rubik text-lg font-normal text-menu";
                      if (isActive) {
                        nameClass = "font-Rubik text-base font-bold text-menu";
                        valueClass = "font-Rubik text-lg font-bold text-menu";
                      }
                      return (
                        <div key={index} className="mb-4 me-2 ms-2">
                          <div
                            className="flex cursor-pointer items-center gap-1.5"
                            onClick={() => {
                              onMouseClick({ name: item.name }, index);
                            }}
                          >
                            <div
                              className={`${circleSize} flex shrink-0 items-center justify-center rounded-full`}
                              style={{ backgroundColor: item.color }}
                            >
                              {item.emoji && (
                                <BudgetCategoryIcon
                                  emoji={item.emoji}
                                  size={isActive ? 18 : 16}
                                  color={
                                    tinycolor(item.color).isDark()
                                      ? "white"
                                      : "black"
                                  }
                                />
                              )}
                            </div>
                            <p className={nameClass}>{item.name}</p>
                          </div>
                          <p className={valueClass}>
                            {formatValue(item.value || 0, {
                              maximumFractionDigits: 0,
                            })}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ChartContainer>
    </div>
  );
};

export default TransactionSummaryChart;

export const hash = _hash;
export const stringToColor = _stringToColor;
export const fantasyPallet = CATEGORY_PALETTE;

export function generateExpensesByCategoryData(
  lang: Language,
  transactions: Transaction[],
  fiat: Fiat[],
  fiatHistory?: FiatRateHistory
) {
  const categoryData: any[] = [];

  // Create categories based on category.calculatedCategory
  transactions.forEach((transaction) => {
    let categoryName = generateCategoryName(lang, transaction);
    const categoryExists = categoryData.some(
      (item) => item.name === categoryName
    );

    if (!categoryExists) {
      const color =
        transaction.budgetCategoryColor || stringToColor(categoryName);
      categoryData.push({
        name: categoryName,
        value: 0,
        color,
        label: categoryName,
        emoji: transaction.budgetCategoryEmoji || null,
      });
    }
  });

  // Sum chargedAmount.amount into the "value" field
  transactions.forEach((transaction) => {
    let categoryName = generateCategoryName(lang, transaction);
    const categoryIndex = categoryData.findIndex(
      (item) => item.name === categoryName
    );

    if (categoryIndex !== -1) {
      categoryData[categoryIndex].value += Math.abs(
        getChargedAmmountInILS(transaction, fiat, fiatHistory)
      );
    }
  });
  categoryData.sort((a, b) => b.value - a.value);

  return categoryData;
}

function renderActiveShape(props: PieSectorDataItem) {
  const RADIAN = Math.PI / 180;
  const {
    cx = 0,
    cy = 0,
    innerRadius,
    outerRadius = 0,
    startAngle,
    endAngle = 0,
    midAngle = 0,
    fill,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + 5 * cos;
  const sy = cy + 5 * sin;
  return (
    <Sector
      cx={sx}
      cy={sy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      cornerRadius={6}
    />
  );
}

function getCategoryIndexByName(
  categoryDataArray: { name: string }[],
  category: string
): SetStateAction<number | undefined> {
  const idx = categoryDataArray.findIndex((item) => item.name === category);
  return idx === -1 ? undefined : idx;
}
